import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  LayoutDashboard,
} from "lucide-react";

/* ===================== STYLES ===================== */
import styles from "./PaymentSuccess.module.scss";

const PaymentSuccessModal = () => {
  const receiptRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const payment = state?.payment;

  /* =========================
     HANDLERS
  ========================= */
  const handleDownloadReceipt = () => {
    const element = receiptRef.current;

    if (!element) return;

    html2pdf()
      .set({
        margin: 0.5,
        filename: `Receipt_${payment.paymentId}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .save();
  };

  /* =========================
     EFFECTS
  ========================= */
  // Direct access protection - redirect if no payment state exists
  useEffect(() => {
    if (!payment) {
      navigate("/billing/pricing", { replace: true });
    }
  }, [payment, navigate]);

  if (!payment) return null;

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-pay-success__page"]}>
      {/* BACK NAVIGATION */}
      <div className={styles["admin-pay-success__header"]}>
        <Link
          to="/billing"
          className={styles["admin-pay-success__back-btn"]}
          aria-label="Go back to billing"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
      </div>

      {/* RECEIPT CARD (Target for PDF Generation) */}
      <div className={styles["admin-pay-success__card"]} ref={receiptRef}>
        <div className={styles["admin-pay-success__icon-wrapper"]}>
          <CheckCircle size={40} strokeWidth={2.5} />
        </div>

        <h1 className={styles["admin-pay-success__title"]}>
          Payment Successful
        </h1>
        <p className={styles["admin-pay-success__subtitle"]}>
          Your payment has been processed successfully.
        </p>

        <div className={styles["admin-pay-success__details"]}>
          <div className={styles["admin-pay-success__detail-row"]}>
            <span className={styles["admin-pay-success__detail-label"]}>
              Amount Paid
            </span>
            <span
              className={`${styles["admin-pay-success__detail-value"]} ${styles["admin-pay-success__detail-value--amount"]}`}
            >
              ₹{payment.amountTotal?.toLocaleString("en-IN") || "0"}
            </span>
          </div>

          <div className={styles["admin-pay-success__detail-row"]}>
            <span className={styles["admin-pay-success__detail-label"]}>
              Date
            </span>
            <span className={styles["admin-pay-success__detail-value"]}>
              {new Date(payment.createdAt).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>

          <div className={styles["admin-pay-success__detail-row"]}>
            <span className={styles["admin-pay-success__detail-label"]}>
              Transaction ID
            </span>
            <span className={styles["admin-pay-success__detail-value"]}>
              {payment.paymentId}
            </span>
          </div>

          <div className={styles["admin-pay-success__detail-row"]}>
            <span className={styles["admin-pay-success__detail-label"]}>
              Payment Method
            </span>
            <span className={styles["admin-pay-success__detail-value"]}>
              Online
            </span>
          </div>
        </div>
      </div>

      {/* ACTIONS (Outside of PDF target) */}
      <div className={styles["admin-pay-success__actions"]}>
        <button
          className={`${styles["admin-pay-success__btn"]} ${styles["admin-pay-success__btn--secondary"]}`}
          onClick={handleDownloadReceipt}
        >
          <Download size={18} strokeWidth={2.5} />
          Download Receipt
        </button>

        <Link
          to="/dashboard"
          className={`${styles["admin-pay-success__btn"]} ${styles["admin-pay-success__btn--primary"]}`}
        >
          <LayoutDashboard size={18} strokeWidth={2.5} />
          Go To Dashboard
        </Link>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;
